import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, TriangleAlert } from "lucide-react";
import { AgentConnectHelp } from "./AgentConnectHelp";

interface AgentKeyRevealProps {
  keyValue: string;
  onDone: () => void;
}

/** The raw key, shown once. The caller drops it from its state when `onDone` runs. */
export function AgentKeyReveal({ keyValue, onDone }: AgentKeyRevealProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const handleCopy = async () => {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(keyValue);
      setCopied(true);
    } catch (err) {
      // Never log the key.
      console.error("Agent key copy failed", (err as Error).name);
      setCopyError(true);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div role="alert" className="p-3 rounded-lg border text-sm flex items-start gap-2 bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300">
        <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
        {t("agent.key.warning")}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("agent.key.label")}</label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={keyValue}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-lg bg-muted text-sm font-mono"
          />
          <button type="button" onClick={handleCopy} className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-accent transition-colors text-sm">
            <Copy className="h-4 w-4" />
            {copied ? t("agent.key.copied") : t("agent.key.copy")}
          </button>
        </div>
        {copyError && <p className="mt-1 text-xs text-red-600">{t("agent.key.copyFailed")}</p>}
      </div>

      <AgentConnectHelp />

      <div className="flex justify-end">
        <button type="button" onClick={onDone} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          {t("agent.key.done")}
        </button>
      </div>
    </div>
  );
}
